import {produce} from 'immer';
import type {
  Interaction,
  Timeline,
  TimelineAction,
} from '@packages/systems/ix3/core';
import {
  type IdMap,
  remapActionTarget,
  remapTrigger,
} from '@packages/systems/ix3/shared';
import {remapMouseFollowActionGroupsInTimelines} from '@packages/systems/ix3-wf-plugins/shared';
import {ElementPreset} from '@packages/systems/wfdl';
import type {IX3State} from '../types';
import {
  generateInteractionId,
  generateTimelineActionId,
  generateTimelineId,
} from '../utils';

type ImportIx3FromElementPresetParams = {
  elementPreset: ElementPreset.ElementPresetType;
  idMap: IdMap;
  styleBlocksIdMap?: IdMap;
  currentPageId: string;
  libraryProfileId?: string;
};

/**
 * Copies IX3 interactions and timelines from an element preset into site state,
 * remapping ids/targets to match the local component import. Used by
 * `createLibraryComponent` (reference-first local copy) and `handleElementAdded`
 * (canvas add). Returns `state` unchanged when the preset has no IX3 interactions.
 */
export function importIx3FromElementPreset(
  state: IX3State,
  {
    elementPreset,
    idMap,
    styleBlocksIdMap = {},
    currentPageId,
    libraryProfileId,
  }: ImportIx3FromElementPresetParams
): IX3State {
  const elementPresetIX3 = ElementPreset.getIX3(elementPreset) ?? null;
  if (!elementPresetIX3 || elementPresetIX3.interactions.length === 0) {
    return state;
  }

  return produce(state, (draft) => {
    const timelineIdMap: IdMap = {};
    const sourceTimelines = remapMouseFollowActionGroupsInTimelines(
      elementPresetIX3.timelines,
      {
        generateGroupId: generateTimelineActionId,
        mapAction: (timelineAction: TimelineAction) => {
          if (!timelineAction.targets) return timelineAction;

          return {
            ...timelineAction,
            targets: timelineAction.targets.map((target) =>
              remapActionTarget(target, idMap, styleBlocksIdMap)
            ),
          };
        },
      }
    );

    sourceTimelines.forEach((timeline) => {
      timelineIdMap[timeline.id] = generateTimelineId();
    });

    sourceTimelines.forEach((timeline) => {
      const newTimelineId = timelineIdMap[timeline.id];
      if (!newTimelineId) {
        return;
      }

      const newTimeline: Timeline = {
        ...timeline,
        id: newTimelineId,
        pageId: currentPageId,
      };

      draft.timelines[newTimeline.id] = newTimeline;
    });

    elementPresetIX3.interactions.forEach((interaction) => {
      const newInteractionId = generateInteractionId();
      const newTimelineIds = interaction.timelineIds?.map(
        (oldId) => timelineIdMap[oldId] || oldId
      );

      const remappedTriggers =
        interaction.triggers?.map((trigger) =>
          remapTrigger(trigger, idMap, styleBlocksIdMap)
        ) ?? interaction.triggers;

      let newScope = interaction.scope;
      if (interaction.scope?.type === 'component') {
        const oldComponentId = interaction.scope.componentId;
        const newComponentId = idMap[oldComponentId] || oldComponentId;

        newScope = {
          ...interaction.scope,
          componentId: newComponentId,
          ...(libraryProfileId ? {libraryProfileId} : {}),
        };
      }

      const newInteraction: Interaction = {
        ...interaction,
        id: newInteractionId,
        pageId: currentPageId,
        timelineIds: newTimelineIds,
        triggers: remappedTriggers,
        scope: newScope,
      };

      draft.interactions[newInteractionId] = newInteraction;
    });
  });
}
